import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Edit } from "lucide-react";
import { useCameraDetails } from "@/hooks/useCameraDetails";
import { CameraDetailsSkeleton } from "@/components/camera/CameraDetailsSkeleton";
import { CameraDetailsError } from "@/components/camera/CameraDetailsError";
import { CameraInfoCard } from "@/components/camera/CameraInfoCard";
import { CompatibleLensesSection } from "@/components/camera/CompatibleLensesSection";
import { CompatibleFilmsSection } from "@/components/camera/CompatibleFilmsSection";
import { EditCameraDialog } from "@/components/camera/EditCameraDialog";
import { isFilmCamera } from "@/utils/cameraUtils";
import { useTranslation } from "@/hooks/useTranslation";
import { motion, AnimatePresence } from "framer-motion";

export default function CameraDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  
  const {
    camera,
    compatibleLenses,
    compatibleFilms,
    loading,
    error,
    hasAdapter,
    setHasAdapter,
    adapterMounts,
    isEditDialogOpen,
    setIsEditDialogOpen,
    isLensesOpen,
    setIsLensesOpen,
    isFilmOpen,
    setIsFilmOpen,
    form,
    toggleAdapterCompatibility,
    handleAddAdapterMount,
    handleRemoveAdapterMount,
    handleLensUpdate,
    handleUpdateCamera,
    fetchCompatibleFilm,
    formatFormat,
    formatMount
  } = useCameraDetails(id);

  const handleBackClick = () => {
    navigate('/inventory?tab=camera');
  };

  const handleEditCamera = () => {
    setIsEditDialogOpen(true);
  };

  const canHaveInterchangeableLenses = () => {
    if (!camera) return false;
    return camera.camera_type !== 'point_and_shoot';
  };

  const isFilmBasedCamera = () => {
    if (!camera) return false;
    return isFilmCamera(camera.camera_type);
  };

  return (
    <AnimatePresence mode="wait">
      {loading ? (
        <motion.div
          key="loading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <CameraDetailsSkeleton onBackClick={handleBackClick} />
        </motion.div>
      ) : error || !camera ? (
        <motion.div
          key="error"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <CameraDetailsError error={error} onBackClick={handleBackClick} />
        </motion.div>
      ) : (
        <motion.div
          key="content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="container py-6 space-y-6 max-w-[1200px] mx-auto"
        >
          <div className="flex items-center justify-between gap-2 mb-6">
            <Button variant="outline" size="sm" onClick={handleBackClick}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('back_to_cameras')}
            </Button>
            
            <Button onClick={handleEditCamera} size="sm">
              <Edit className="h-4 w-4 mr-1" />
              {t('edit_camera')}
            </Button>
          </div>
          
          <EditCameraDialog
            isOpen={isEditDialogOpen}
            setIsOpen={setIsEditDialogOpen}
            camera={camera}
            form={form}
            onSubmit={handleUpdateCamera}
          />
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-6">
              <CameraInfoCard
                camera={camera}
                hasAdapter={hasAdapter}
                setHasAdapter={setHasAdapter}
                adapterMounts={adapterMounts}
                handleAddAdapterMount={handleAddAdapterMount}
                handleRemoveAdapterMount={handleRemoveAdapterMount}
                toggleAdapterCompatibility={toggleAdapterCompatibility}
              />
            </div>
            
            <div className="lg:col-span-2">
              {canHaveInterchangeableLenses() && (
                <div className="mb-6">
                  <CompatibleLensesSection
                    isLensesOpen={isLensesOpen}
                    setIsLensesOpen={setIsLensesOpen}
                    compatibleLenses={compatibleLenses}
                    cameraMount={camera.mount}
                    hasAdapter={hasAdapter}
                    adapterMounts={adapterMounts}
                    formatMount={formatMount}
                    handleLensUpdate={handleLensUpdate}
                  />
                </div>
              )}

              {isFilmBasedCamera() && (
                <div>
                  <CompatibleFilmsSection
                    isFilmOpen={isFilmOpen}
                    setIsFilmOpen={setIsFilmOpen}
                    compatibleFilms={compatibleFilms}
                    cameraFormat={camera.format}
                    formatFormat={formatFormat}
                    onActionComplete={() => fetchCompatibleFilm(camera.format)}
                  />
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
